import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { preTreatmentsService } from "../../services/reference/preTreatments.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, formatReferenceSourceDisplay, ListPageTitles, recordLifecycleListConfirmBodies, resolveActionErrorMessage, showToastSuccess, appRoutes } from "../../utils";
import {
    buildPtAdminPagedListActions,
    getPtAdminActionSuccessMessage,
    type PtAdminConfirmKind,
} from "./ptAdminListActions";

type ConfirmKind = PtAdminConfirmKind | null;

const PreTreatmentsListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmPtId, setconfirmPtId] = useState<string | null>(null);
    const [confirmPtLabel, setconfirmPtLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setconfirmPtId(null);
        setconfirmPtLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmPtId || !confirmKind) return;
        const id = confirmPtId;
        const op =
            confirmKind === "disable"
                ? preTreatmentsService.disable(id)
                : confirmKind === "enable"
                  ? preTreatmentsService.enable(id)
                  : confirmKind === "softDelete"
                    ? preTreatmentsService.softDelete(id)
                    : preTreatmentsService.restore(id);
        op.then(() => {
            showToastSuccess(getPtAdminActionSuccessMessage(confirmKind, confirmPtLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; code: string }) => {
        setConfirmKind(kind);
        setconfirmPtId(item.id);
        setconfirmPtLabel(item.code);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.preTreatment,
                confirmPtLabel,
                recordLifecycleListConfirmBodies(confirmPtLabel)
            ),
        [confirmKind, confirmPtLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "code",
                renderAs: ColumnRender.customRenderer,
                title: "Code",
                width: 120,
                customRenderer: (item: { code?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{item.code ?? ""}</span>
                    </td>
                ),
            } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 320 } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Code", member: "code", type: FilterType.text } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(`/pre-treatments/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildPtAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    const headActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: addEntityActionLabel(EntityLabels.preTreatment),
                icon: faPlus,
                onClick: () => navigate("/pre-treatments/new"),
                primary: true,
            } as IPagedListAction,
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.preTreatments}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.preTreatments },
                        ]}
                    />
                    <h1>{ListPageTitles.preTreatments}</h1>
                    <p className="text-muted">
                        Pre-treatment values for regulatory reporting. Administrators can add, edit, disable, or remove pre-treatments.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Pre-treatment actions"
                        showDeletedRowIndicator
                        defaultSortBy="Code"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/pre-treatments/all"
                        rowFormatter={() => null}
                        persistanceKey="PreTreatmentsListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { PreTreatmentsListPage };

